import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { keyframes } from "@emotion/react";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  cancelTimeOffRequest,
  createTimeOffRequest,
  updateTimeOffRequest,
} from "../../../api/TimeTracking/timeTrackingApi";
import { ABSENCE_TYPES } from "./absencesMonthUtils";
import { ttFilterFieldSx } from "./timeTrackingStyles";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const pop = keyframes`
  0%, 12% { opacity: 0.35; transform: translateY(6px) scale(0.92); }
  35%, 68% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0.45; transform: translateY(3px) scale(0.96); }
`;

const floatDay = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

function AbsencePreviewStage({ accent, editing }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 78,
        mt: 1.35,
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        background: `linear-gradient(120deg, ${alpha(accent, 0.14)}, ${alpha(
          accent,
          0.05,
        )}, ${alpha(accent, 0.16)})`,
        backgroundSize: "200% 200%",
        animation: `${rise} 320ms ease both, ${shimmer} 5.5s ease-in-out infinite`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 14,
          top: 12,
          width: 46,
          height: 54,
          borderRadius: 1.5,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.4),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.18)}`,
          animation: `${pop} 3.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {editing ? (
          <EditCalendarOutlinedIcon sx={{ fontSize: 22, color: accent }} />
        ) : (
          <BeachAccessOutlinedIcon sx={{ fontSize: 22, color: accent }} />
        )}
      </Box>

      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 76 + i * 34,
            top: 18,
            width: 28,
            height: 42,
            borderRadius: 1.25,
            bgcolor: alpha("#fff", 0.92),
            border: "1px solid",
            borderColor: alpha(accent, 0.28 + i * 0.08),
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.4,
            animation: `${floatDay} 2.4s ease-in-out infinite`,
            animationDelay: `${i * 0.22}s`,
            ...motionSafe,
          }}
        >
          <Box
            sx={{
              width: 14,
              height: 3,
              borderRadius: 999,
              bgcolor: alpha(accent, 0.35 + i * 0.1),
            }}
          />
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 800,
              color: accent,
              lineHeight: 1,
            }}
          >
            {5 + i}
          </Typography>
        </Box>
      ))}

      <Box
        sx={{
          position: "absolute",
          right: 12,
          bottom: 10,
          width: 28,
          height: 28,
          borderRadius: 1,
          bgcolor: alpha(accent, 0.16),
          border: "1px solid",
          borderColor: alpha(accent, 0.4),
          display: "grid",
          placeItems: "center",
          animation: `${floatDay} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <EventAvailableOutlinedIcon sx={{ fontSize: 16, color: accent }} />
      </Box>
    </Box>
  );
}

const AbsenceAssignDialog = ({
  open,
  onClose,
  onSaved,
  canRrhh,
  employees = [],
  defaultDate,
  existing = null,
}) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const accent = theme.palette.primary.main || "#0f766e";
  const [userId, setUserId] = useState("");
  const [requestType, setRequestType] = useState("vacation");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(existing?.id);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (existing) {
      setRequestType(existing.requestType || "vacation");
      setStartDate(String(existing.startDate || "").slice(0, 10));
      setEndDate(String(existing.endDate || "").slice(0, 10));
      setReason(existing.reason || "");
      setUserId(existing.userId != null ? String(existing.userId) : "");
      return;
    }
    setReason("");
    setRequestType("vacation");
    setStartDate(defaultDate || "");
    setEndDate(defaultDate || "");
    setUserId(canRrhh && employees.length === 1 ? String(employees[0].id) : "");
  }, [open, defaultDate, canRrhh, employees, existing]);

  const handleSave = async () => {
    if (!startDate || !endDate) {
      setError("Indica fechas de inicio y fin");
      return;
    }
    if (canRrhh && !userId && !editing) {
      setError("Selecciona el empleado");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      if (editing) {
        await updateTimeOffRequest(existing.id, { requestType });
      } else {
        await createTimeOffRequest({
          requestType,
          startDate,
          endDate,
          reason: reason.trim() || null,
          ...(canRrhh && userId
            ? { userId: Number(userId), assignAsApproved: true }
            : {}),
        });
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          (editing
            ? "No se pudo actualizar la ausencia"
            : "No se pudo asignar la ausencia"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "¿Cancelar esta ausencia? Dejará de verse en el calendario.",
      )
    ) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await cancelTimeOffRequest(existing.id);
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo cancelar la ausencia");
    } finally {
      setSaving(false);
    }
  };

  const title = editing
    ? "Editar ausencia"
    : canRrhh
      ? "Asignar ausencia"
      : "Solicitar ausencia";
  const subtitle = editing
    ? "Cambia el tipo o elimina el día del calendario"
    : "Marca vacaciones, permiso o teletrabajo en un clic";

  const fieldMotion = (delayMs) => ({
    animation: `${rise} 340ms ease both`,
    animationDelay: `${delayMs}ms`,
    ...motionSafe,
  });

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      fullScreen={isNarrow}
      maxWidth="sm"
      keepMounted={false}
      transitionDuration={180}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: isNarrow ? 0 : 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          maxHeight: isNarrow ? "100%" : "min(92vh, 640px)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.primary.main, 0.14)} 0%, ${alpha(
              th.palette.info.main,
              0.08,
            )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accent, 0.16),
                color: accent,
                flexShrink: 0,
              }}
            >
              <CalendarMonthOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {title}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4, mt: 0.3 }}
              >
                {subtitle}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="Cerrar"
            onClick={onClose}
            disabled={saving}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
        <AbsencePreviewStage accent={accent} editing={editing} />
      </Box>

      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1,
          overflow: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Stack spacing={2}>
          {error ? (
            <Alert severity="error" sx={{ borderRadius: 2, ...fieldMotion(0) }}>
              {error}
            </Alert>
          ) : null}

          {canRrhh ? (
            <FormControl
              size="small"
              fullWidth
              sx={{ ...ttFilterFieldSx(theme), ...fieldMotion(40) }}
            >
              <InputLabel>Empleado</InputLabel>
              <Select
                value={userId}
                label="Empleado"
                disabled={editing}
                onChange={(e) => setUserId(e.target.value)}
              >
                {employees.map((e) => (
                  <MenuItem key={e.id} value={String(e.id)}>
                    {e.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          <FormControl
            size="small"
            fullWidth
            sx={{ ...ttFilterFieldSx(theme), ...fieldMotion(80) }}
          >
            <InputLabel>Tipo</InputLabel>
            <Select
              value={requestType}
              label="Tipo"
              onChange={(e) => setRequestType(e.target.value)}
            >
              {ABSENCE_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={fieldMotion(120)}
          >
            <TextField
              label="Desde"
              type="date"
              size="small"
              fullWidth
              value={startDate}
              disabled={editing}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={ttFilterFieldSx(theme)}
            />
            <TextField
              label="Hasta"
              type="date"
              size="small"
              fullWidth
              value={endDate}
              disabled={editing}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={ttFilterFieldSx(theme)}
            />
          </Stack>

          <TextField
            label="Motivo"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            disabled={editing}
            onChange={(e) => setReason(e.target.value)}
            sx={{ ...ttFilterFieldSx(theme), ...fieldMotion(160) }}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          bgcolor: alpha(theme.palette.background.default, 0.55),
        }}
      >
        {editing ? (
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={handleDelete}
            disabled={saving}
            sx={{ mr: "auto", fontWeight: 700, borderRadius: 2 }}
          >
            Eliminar
          </Button>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ fontWeight: 700, borderRadius: 2 }}
        >
          Cerrar
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ fontWeight: 800, borderRadius: 2, px: 2.25 }}
        >
          Guardar
        </Button>
      </Box>
    </Dialog>
  );
};

export default AbsenceAssignDialog;
